// Externals
import type { Reducer } from 'redux';

// Libraries
import { CLEAR_ROLE_ASSUMPTION } from '@makemydeal/dr-activities-role-assumption';
import { initActionTypes } from '@makemydeal/dr-shared-store';
import { salesCheckInMode } from '@makemydeal/dr-sales-checkin-types';
import type { SCICommonConsumer } from '@makemydeal/dr-sales-checkin-types';

// Types
import type { CustomerState } from './types';

// Utils
import { composeConsumer, mapSelectedCustomer } from '@makemydeal/dr-sales-checkin-ui-utils';
import { utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import { getEnableCorrectPhoneHandling } from './correctPhoneHandling';

// Actions
import { getCustomerInitialState } from './customerInitialState';
import { Actions } from '../actionTypes/actionTypes';
import { SALES_CHECKIN_MODE } from '@makemydeal/dr-sales-checkin-ui-utils';

export const customer: Reducer<CustomerState> = (state: CustomerState = getCustomerInitialState(), action: any) => {
    const { payload, type } = action;

    switch (type) {
        case CLEAR_ROLE_ASSUMPTION: {
            const mode = sessionStorage.getItem(SALES_CHECKIN_MODE);
            if (mode === salesCheckInMode.Standalone) {
                return {
                    ...state,
                    checkedInCustomer: undefined,
                    coBuyer: null,
                    isCoBuyer: false
                };
            }
            return {
                ...state
            };
        }
        case Actions.SET_CO_BUYER: {
            return {
                ...state,
                coBuyer: mapSelectedCustomer(payload.coBuyer)
            };
        }
        case Actions.REMOVE_CO_BUYER: {
            return {
                ...state,
                coBuyer: null
            };
        }

        case Actions.VIEW_CUSTOMER_INFO:
        case Actions.ADD_CO_BUYER: {
            return {
                ...state,
                isCoBuyer: payload.isCoBuyer
            };
        }

        case Actions.FETCH_DEAL_SUCCESS:
        case initActionTypes.INIT_PENCIL_SUCCESS: {
            const dealDetails = payload.deal ? payload.deal.details : payload.data?.deal;
            if (dealDetails) {
                const { commonOrgId } = dealDetails.coxAutoReferences ?? {};
                if (commonOrgId) {
                    const globalParticipants = dealDetails.participants;
                    const buyer = dealDetails.dealStructure.participants.buyer;
                    const coBuyer = dealDetails.dealStructure.participants.coBuyer || null;
                    const enableCorrectPhoneHandling = getEnableCorrectPhoneHandling(action);
                    const buyerFromGlobalParticipants = offerReduxUtils.getParticipant(buyer, globalParticipants, true) || null;
                    const commonConsumer = composeConsumer(
                        buyer,
                        commonOrgId,
                        buyerFromGlobalParticipants,
                        enableCorrectPhoneHandling
                    );

                    let commonCobuyer: SCICommonConsumer | undefined = undefined;
                    if (coBuyer) {
                        const coBuyerFromGlobalParticipants =
                            offerReduxUtils.getParticipant(coBuyer, globalParticipants, true) || null;
                        commonCobuyer = composeConsumer(
                            coBuyer,
                            commonOrgId,
                            coBuyerFromGlobalParticipants,
                            enableCorrectPhoneHandling
                        );
                    }

                    return {
                        ...state,
                        checkedInCustomer: commonConsumer,
                        coBuyer: commonCobuyer ? mapSelectedCustomer(commonCobuyer) : null
                    };
                }
                return {
                    ...state
                };
            }
            return {
                ...state
            };
        }
        case Actions.UPDATE_PARTICIPANT_DETAILS: {
            if (payload.participant === 'coBuyer') {
                return {
                    ...state,
                    coBuyer: mapSelectedCustomer({
                        ...state.coBuyer,
                        ...payload.customer
                    })
                };
            }
            return {
                ...state,
                checkedInCustomer: {
                    ...state.checkedInCustomer,
                    ...payload.customer
                }
            };
        }
        case Actions.PREPARE_DEAL_SETUP_STATE: {
            return {
                ...state,
                checkedInCustomer: {
                    ...state.checkedInCustomer,
                    ...payload.customer
                }
            };
        }
        case Actions.UPDATE_CURRENT_CUSTOMER: {
            return {
                ...state,
                checkedInCustomer: payload.customer
            };
        }
        case Actions.UPDATE_CUSTOMER:
        case Actions.CANCEL_UPDATE_CUSTOMER: {
            return state;
        }

        default: {
            return state;
        }
    }
};
